import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { formatApiError } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Plus, Search, Edit2, Trash2, DollarSign } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccrualsPage = () => {
  const { t, language } = useLanguage();
  const currency = language === 'tr' ? '₺' : '$';
  const [accruals, setAccruals] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    tenant_id: '',
    contract_id: '',
    accrual_type: 'rent',
    description: '',
    amount: '',
    due_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accrualsRes, tenantsRes, contractsRes] = await Promise.all([
        axios.get(`${API_URL}/api/accruals`, { withCredentials: true }),
        axios.get(`${API_URL}/api/tenants`, { withCredentials: true }),
        axios.get(`${API_URL}/api/contracts`, { withCredentials: true })
      ]);
      setAccruals(accrualsRes.data);
      setTenants(tenantsRes.data);
      setContracts(contractsRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
        contract_id: formData.contract_id || null
      };

      await axios.post(`${API_URL}/api/accruals`, payload, { withCredentials: true });
      toast.success(t('saveSuccess'));
      setIsModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/api/accruals/${id}`, { withCredentials: true });
      toast.success(t('deleteSuccess'));
      fetchData();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const resetForm = () => {
    setFormData({
      tenant_id: '',
      contract_id: '',
      accrual_type: 'rent',
      description: '',
      amount: '',
      due_date: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'payment-paid',
      partial: 'payment-partial',
      unpaid: 'payment-unpaid',
      overdue: 'payment-overdue'
    };
    return `px-2 py-1 text-xs font-bold uppercase border ${styles[status] || 'bg-gray-100'}`;
  };

  const getTenantName = (tenantId) => tenants.find(t => t.id === tenantId)?.name || '-';

  const filteredAccruals = accruals.filter(a => {
    const matchesStatus = statusFilter === 'all' || a.status === statusFilter;
    const matchesType = typeFilter === 'all' || a.accrual_type === typeFilter;
    return matchesStatus && matchesType;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accruals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t('accruals')}</h1>
          <p className="text-gray-500 text-sm mt-1">{accruals.length} {t('total')}</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsModalOpen(true); }}
          className="bg-black text-white rounded-none hover:bg-gray-800"
          data-testid="add-accrual-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('addAccrual')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-none">
            <SelectValue placeholder={t('status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="unpaid">{t('unpaid')}</SelectItem>
            <SelectItem value="partial">{t('partial')}</SelectItem>
            <SelectItem value="paid">{t('paid')}</SelectItem>
            <SelectItem value="overdue">{t('overdue')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40 rounded-none">
            <SelectValue placeholder={t('accrualType')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="rent">{t('rent')}</SelectItem>
            <SelectItem value="water">{t('water')}</SelectItem>
            <SelectItem value="internet">{t('internet')}</SelectItem>
            <SelectItem value="deposit">{t('deposit')}</SelectItem>
            <SelectItem value="aidat">{t('aidat')}</SelectItem>
            <SelectItem value="extra_charge">{t('extraCharge')}</SelectItem>
            <SelectItem value="discount">{t('discount')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Accruals Table */}
      <div className="border border-gray-200 overflow-x-auto">
        <table className="data-table">
          <thead>
            <tr className="bg-gray-50">
              <th>{t('tenants')}</th>
              <th>{t('accrualType')}</th>
              <th>{t('description')}</th>
              <th>{t('amount')}</th>
              <th>{t('paidAmount')}</th>
              <th>{t('remainingAmount')}</th>
              <th>{t('dueDate')}</th>
              <th>{t('status')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredAccruals.length > 0 ? (
              filteredAccruals.map((accrual) => (
                <tr key={accrual.id} data-testid={`accrual-row-${accrual.id}`}>
                  <td className="font-medium">{getTenantName(accrual.tenant_id)}</td>
                  <td className="uppercase text-xs">{t(accrual.accrual_type) || accrual.accrual_type}</td>
                  <td>{accrual.description || '-'}</td>
                  <td className="font-mono">{currency}{accrual.amount}</td>
                  <td className="font-mono text-emerald-600">{currency}{accrual.paid_amount}</td>
                  <td className="font-mono text-red-600">{currency}{accrual.remaining_amount}</td>
                  <td>{accrual.due_date?.split('T')[0]}</td>
                  <td>
                    <span className={getStatusBadge(accrual.status)}>
                      {t(accrual.status) || accrual.status}
                    </span>
                  </td>
                  <td>
                    <button 
                      onClick={() => handleDelete(accrual.id)} 
                      className="p-1 hover:bg-red-100 text-red-600"
                      data-testid={`delete-accrual-${accrual.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="text-center py-8 text-gray-500">
                  {t('noData')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">
              {t('addAccrual')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">{t('tenants')} *</label>
              <Select 
                value={formData.tenant_id} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, tenant_id: val }))}
              >
                <SelectTrigger className="rounded-none mt-1" data-testid="accrual-tenant-select">
                  <SelectValue placeholder={t('selectTenant')} />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('accrualType')} *</label>
                <Select 
                  value={formData.accrual_type} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, accrual_type: val }))}
                >
                  <SelectTrigger className="rounded-none mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rent">{t('rent')}</SelectItem>
                    <SelectItem value="water">{t('water')}</SelectItem>
                    <SelectItem value="internet">{t('internet')}</SelectItem>
                    <SelectItem value="deposit">{t('deposit')}</SelectItem>
                    <SelectItem value="aidat">{t('aidat')}</SelectItem>
                    <SelectItem value="extra_charge">{t('extraCharge')}</SelectItem>
                    <SelectItem value="discount">{t('discount')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">{t('amount')} *</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  required
                  className="rounded-none mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('dueDate')} *</label>
              <Input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                required
                className="rounded-none mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('description')}</label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="rounded-none mt-1"
              />
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-none">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-black text-white rounded-none hover:bg-gray-800" data-testid="accrual-submit-btn">
                {t('save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccrualsPage;
